import React from 'react'
import { FormattedMessage } from 'react-intl'
import { css } from '@emotion/css'
import { HttpRequest, UserInput, AttackEventDetails } from '../shared'
import { AttackEventRequest } from '../attack-event-request'
import { AttackStatus } from '../../attack-status'
import {
  NewSuspiciousEventDescription,
  LegacySuspiciousEventDescription,
  AttackEventResultCodeSnippet
} from '../attack-event-overview-view'
import { ContrastFormattedMessage } from '../../contrast-formatted-message'

export enum CmdInjectionFindings {
  CHAINING = 'CHAINING',
  BACKDOOR = 'BACKDOOR',
  PATH_ARGUMENT = 'PATH_ARGUMENT'
}

const CmdInjectionFindingsi18lnConfig: Record<CmdInjectionFindings, string> = {
  [CmdInjectionFindings.CHAINING]: 'detail_attackEventFindings_chainedCommands',
  [CmdInjectionFindings.BACKDOOR]:
    'detail_attackEventFindings_parameterCommandBackdoors',
  [CmdInjectionFindings.PATH_ARGUMENT]:
    'detail_attackEventFindings_dangerousPaths'
}

export interface ICommandInjectionAnalysisOverviewDetails {
  readonly command: string
  readonly start?: number
  readonly end?: number
  readonly findings: string[]
}

const containerCss = css`
  padding-bottom: 20px;
`

export function CmdInjectionChildRuleSemanticAnalysisOverview({
  httpRequest,
  userInput,
  details,
  sourceIP,
  result,
  cmdInjectionFinding
}: {
  readonly httpRequest?: HttpRequest
  readonly userInput?: UserInput
  readonly sourceIP?: string
  readonly details: ICommandInjectionAnalysisOverviewDetails
  readonly result: AttackStatus
  readonly cmdInjectionFinding: CmdInjectionFindings
}) {
  const command = !!details.command ? details.command : ''

  return (
    <React.Fragment>
      <CmdInjectionSemanticAnalysisRuleSourceOverview
        request={httpRequest}
        sourceIP={sourceIP}
        userInput={userInput}
      />
      <CmdInjectionSemanticAnalysisRuleResultOverview
        overviewTypeText={
          <b>
            <ContrastFormattedMessage
              id={CmdInjectionFindingsi18lnConfig[cmdInjectionFinding]}
              children={(msg) => msg.toLowerCase()}
            />
          </b>
        }
        details={{ command, start: details.start, end: details.end }}
      />
      <CmdInjectionSemanticAnalysisRuleNoSourceOverview sourceIP={sourceIP} />
      {result === AttackStatus.SUSPICIOUS && (
        <NewSuspiciousEventDescription
          findingTranslationId={
            CmdInjectionFindingsi18lnConfig[cmdInjectionFinding]
          }
          containerCss={containerCss}
        />
      )}
    </React.Fragment>
  )
}

export function CmdInjectionParentRuleSemanticAnalysisOverview({
  httpRequest,
  userInput,
  details,
  sourceIP,
  result
}: {
  readonly httpRequest?: HttpRequest
  readonly userInput?: UserInput
  readonly sourceIP?: string
  readonly details: AttackEventDetails
  readonly result: AttackStatus
}) {
  const command = !!details.command ? details.command : ''
  const findings = details.findings
    ? details.findings.map((finding) => CmdInjectionFindings[finding])
    : []
  return (
    <React.Fragment>
      <CmdInjectionSemanticAnalysisRuleSourceOverview
        request={httpRequest}
        sourceIP={sourceIP}
        userInput={userInput}
      />
      <CmdInjectionSemanticAnalysisRuleResultOverview
        overviewTypeText={
          <FormattedMessage id="detail_attackEventFindings_suspiciousElements" />
        }
        details={{ command, start: details.start, end: details.end }}
      />
      <CmdInjectionSemanticAnalysisRuleNoSourceOverview sourceIP={sourceIP} />
      <LegacySuspiciousEventDescription
        containerCss={containerCss}
        result={result}
      />
      <CmdInjectionSemanticAnalysisRuleDetailsOverview details={{ findings }} />
    </React.Fragment>
  )
}

export function CmdInjectionSemanticAnalysisRuleSourceOverview({
  request,
  userInput,
  sourceIP
}: {
  readonly request?: HttpRequest
  readonly userInput?: UserInput
  readonly sourceIP?: string
}) {
  return (
    <React.Fragment>
      {sourceIP && userInput && request && (
        <div className={containerCss}>
          <FormattedMessage id="detail_attackEventSource_observingHttpRequest" />
          <AttackEventRequest
            request={request}
            userInput={userInput}
            verbose={false}
            showTaintedValues={false}
          />
        </div>
      )}
    </React.Fragment>
  )
}

export function CmdInjectionSemanticAnalysisRuleNoSourceOverview({
  sourceIP
}: {
  readonly sourceIP?: string
}) {
  return (
    <React.Fragment>
      {!sourceIP && (
        <div className={containerCss}>
          <FormattedMessage id="detail_attackEventSource_sourceUnknownNoHttpRequest" />
        </div>
      )}
    </React.Fragment>
  )
}

export function CmdInjectionSemanticAnalysisRuleResultOverview({
  overviewTypeText,
  details: { command, start, end }
}: {
  readonly overviewTypeText: JSX.Element | string
  readonly details: {
    readonly command?: string
    readonly start?: number
    readonly end?: number
  }
}) {
  return (
    <React.Fragment>
      {command && (
        <div className={containerCss}>
          <FormattedMessage
            id="detail_attackEventResult_suspiciousElementsInCommand"
            values={{ overviewType: overviewTypeText }}
          />

          <AttackEventResultCodeSnippet
            result={command}
            start={start}
            end={end}
          />
        </div>
      )}
    </React.Fragment>
  )
}

export function CmdInjectionSemanticAnalysisRuleDetailsOverview({
  details: { findings }
}: {
  readonly details: {
    readonly findings: CmdInjectionFindings[]
  }
}) {
  return (
    <React.Fragment>
      {Array.isArray(findings) && findings.length > 0 && (
        <div className={containerCss}>
          <FormattedMessage
            id="detail_attackEventDetails_systemCommandContainsSuspiciousElementPlural"
            values={{ count: findings.length }}
          />
          <ul>{findings.map((finding) => renderFindingsList(finding))}</ul>
        </div>
      )}
    </React.Fragment>
  )
}

const renderFindingsList = (finding: CmdInjectionFindings) => {
  const findingID =
    finding && CmdInjectionFindingsi18lnConfig[finding]
      ? CmdInjectionFindingsi18lnConfig[finding]
      : undefined

  return (
    findingID && (
      <React.Fragment key={findingID}>
        {findingID && (
          <li>
            <FormattedMessage id={findingID} />
          </li>
        )}
      </React.Fragment>
    )
  )
}
